import {faPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Group} from '@mantine/core';
import {useCallback} from 'react';
import {Link} from 'react-router-dom';
import {ActionClassService} from '../api/generated/ActionClassService';
import {ActionClassSummaryDto} from '../api/generated/index.defs';
import {BasicPage} from '../components/basic-page';
import {
  getActiveFiltersCount,
  PagedDataTableConfig,
  renderEditButton,
  usePagedDataTable,
} from '../hooks/paged-data-table/use-paged-data-table';
import {useProduce} from '../hooks/use-produce';
import {ROUTES} from '../routes';
import {AdvancedPagedRequest} from '../types/index.ts';
import {
  ComponentState,
  FilterSchema,
  TableFilters,
} from './action-class-listing-filter';

const RuntimeConfig = {
  renderActions: (
    <Button component={Link} to={ROUTES.ADMIN.ACTION_CLASSES.CREATE.path}>
      <Group gap={'xs'}>
        <FontAwesomeIcon icon={faPlus} /> New Action Class
      </Group>
    </Button>
  ),
};

export const ActionClassListing = () => {
  const [state, setState] = useProduce<ComponentState>({
    filter: {
      class: '',
      actionCodeId: undefined,
      levelOfServiceCodeId: undefined,
    },
  });

  const additionalParams = state.filter;

  const onStateChange = useCallback(
    (tableState: AdvancedPagedRequest<ActionClassSummaryDto>) => {
      setState((draft) => {
        draft.tableState = tableState;
      });
    },
    [setState]
  );

  const clearFilterState = useCallback(() => {
    setState((draft) => {
      draft.filter.class = '';
      draft.filter.actionCodeId = undefined;
      draft.filter.levelOfServiceCodeId = undefined;
    });
  }, [setState]);

  const pagedDataTable = usePagedDataTable(
    ActionClassService.getAll,
    TableConfig,
    {
      ...RuntimeConfig,
      additionalParams,
      onStateChange,
      clearFilterState,
      renderFilter: (dismiss) => (
        <TableFilters
          defaultValues={state.filter as FilterSchema}
          onSubmit={(filters) => {
            setState((draft) => {
              draft.filter.actionCodeId = filters.actionCodeId;
              draft.filter.levelOfServiceCodeId = filters.levelOfServiceCodeId;
              draft.filter.class = filters.class;
            });

            dismiss();
          }}
        />
      ),
      filterBadge: getActiveFiltersCount(state.filter),
    }
  );

  return <BasicPage title="Action Classes">{pagedDataTable}</BasicPage>;
};

const TableConfig = PagedDataTableConfig(ActionClassService.getAll, {
  columns: [
    {
      accessor: '',
      title: '',
      render: (item) =>
        renderEditButton({
          item: {...item, id: Number(item.id)},
          descriptor: 'Action Class',
          route: ROUTES.ADMIN.ACTION_CLASSES.DETAIL.path,
        }),
      width: 50,
    },
    {
      accessor: 'actionCodeDescription',
      title: 'Action Code',
      sortable: true,
    },
    {
      accessor: 'levelOfServiceCodeDescription',
      title: 'Level of Service',
      sortable: true,
    },
    {
      accessor: 'epicorClassDescription',
      title: 'Part Class',
    },
    {
      accessor: 'canBeChanged',
      title: 'Can be Changed',
      render: (item) => (item.canBeChanged ? 'Yes' : 'No'),
    },
  ],
  defaultSort: {
    column: 'levelOfServiceCodeDescription',
    direction: 'ASC',
  },
  searchFieldNames: ['actionCodeDescription'],
});
